<script setup lang="ts">
import { computed, onBeforeUnmount, ref } from "vue";
import { processTriggerMachineOutput, SYMBOL_POOL } from "./utils/processTriggerMachineOutput";

const displayedSymbols = ref<string[]>(["Pean", "ut", "bo"]);
const isSpinning = ref(false);
const leverActive = ref(false);
const reelSpinning = ref<boolean[]>([false, false, false]);
const history = ref<string[][]>([]);

const spinIntervals = ref<Array<number | null>>([null, null, null]);
const stopTimeouts = ref<Array<number | null>>([null, null, null]);
let leverResetTimeout: number | null = null;

const statusMessage = computed(() =>
  isSpinning.value ? "PeanPeanPeanutbo~" : "Feel lucky?"
);

const lastResultText = computed(() => {
  if (history.value.length > 0) {
    return history.value[0].join(" | ");
  }
  return displayedSymbols.value.join(" | ");
});

function getInterimSymbol(current: string): string {
  const poolSize = SYMBOL_POOL.length;
  if (poolSize === 0) {
    return current;
  }

  let nextIndex = Math.floor(Math.random() * poolSize);
  if (SYMBOL_POOL[nextIndex] === current) {
    nextIndex = (nextIndex + 1) % poolSize;
  }

  return SYMBOL_POOL[nextIndex];
}

function activateLeverAnimation(): void {
  leverActive.value = true;
  if (leverResetTimeout !== null) {
    window.clearTimeout(leverResetTimeout);
  }

  leverResetTimeout = window.setTimeout(() => {
    leverActive.value = false;
    leverResetTimeout = null;
  }, 600);
}

function clearSpinIntervals(): void {
  spinIntervals.value.forEach((intervalId, index) => {
    if (intervalId !== null) {
      window.clearInterval(intervalId);
      spinIntervals.value[index] = null;
    }
  });
}

function clearStopTimeouts(): void {
  stopTimeouts.value.forEach((timeoutId, index) => {
    if (timeoutId !== null) {
      window.clearTimeout(timeoutId);
      stopTimeouts.value[index] = null;
    }
  });
}

function finishSpin(result: [string, string, string]): void {
  history.value = [[...result], ...history.value].slice(0, 1);
  isSpinning.value = false;
}

function pullLever(): void {
  if (isSpinning.value) {
    return;
  }

  activateLeverAnimation();
  isSpinning.value = true;
  reelSpinning.value = [true, true, true];

  clearSpinIntervals();
  clearStopTimeouts();

  const finalSymbols = processTriggerMachineOutput();
  const spinSpeeds = [70, 90, 110];
  const stopDelays = [1400, 1800, 2200];

  finalSymbols.forEach((_, index) => {
    const intervalSpeed = spinSpeeds[index] ?? 90;
    spinIntervals.value[index] = window.setInterval(() => {
      displayedSymbols.value[index] = getInterimSymbol(displayedSymbols.value[index]);
    }, intervalSpeed);
  });

  finalSymbols.forEach((symbol, index) => {
    const delay = stopDelays[index] ?? 1800;
    stopTimeouts.value[index] = window.setTimeout(() => {
      const intervalId = spinIntervals.value[index];
      if (intervalId !== null) {
        window.clearInterval(intervalId);
        spinIntervals.value[index] = null;
      }

      displayedSymbols.value[index] = symbol;
      reelSpinning.value[index] = false;

      if (index === finalSymbols.length - 1) {
        finishSpin(finalSymbols);
      }
    }, delay);
  });
}

onBeforeUnmount(() => {
  clearSpinIntervals();
  clearStopTimeouts();
  if (leverResetTimeout !== null) {
    window.clearTimeout(leverResetTimeout);
  }
});
</script>

<template>
  <div class="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
    <main
      class="mx-auto flex min-h-screen w-full max-w-5xl flex-col items-center justify-center px-4 py-10"
    >
      <h1 class="text-center text-4xl font-bold text-casino-accent drop-shadow-md md:text-5xl">
        Peanutbo 自动机
      </h1>
      <p class="mt-3 text-center text-base text-slate-300 md:text-lg">
        {{ statusMessage }}
      </p>

      <section class="mt-8 w-full">
        <div
          class="machine-shell mx-auto flex w-full max-w-4xl flex-col items-center gap-10 md:flex-row md:items-center md:justify-center"
        >
          <div class="machine-body">
          <div class="grid w-full grid-cols-3 gap-4" aria-live="polite" aria-atomic="true">
            <div
              v-for="(symbol, index) in displayedSymbols"
              :key="`reel-${index}`"
              :class="['reel-window', { 'reel-window--spinning': reelSpinning[index] }]"
            >
              <span class="reel-symbol">{{ symbol }}</span>
            </div>
          </div>

          <div class="mt-6 w-full rounded-2xl border border-slate-800/70 bg-slate-900/70 p-4">
            <h2 class="text-sm font-semibold uppercase tracking-[0.3em] text-slate-400">
              最近结果
            </h2>
            <ul class="recent-results">
              <li
                v-for="(entry, idx) in history"
                :key="`history-${idx}`"
                class="recent-result-row"
              >
                <span
                  v-for="(symbol, symbolIdx) in entry"
                  :key="`history-${idx}-symbol-${symbolIdx}`"
                  class="recent-result-pill"
                >
                  {{ symbol }}
                </span>
              </li>
              <li v-if="history.length === 0" class="text-slate-500">
                Pull the lever to record your first spin.
              </li>
            </ul>
          </div>
          </div>

          <button
            class="lever-crank"
            :class="{
              'lever-crank--active': leverActive,
              'lever-crank--locked': isSpinning
            }"
            @click="pullLever"
            :disabled="isSpinning"
            aria-label="Pull the lever to spin the slot machine"
            type="button"
          >
            <span class="sr-only">Pull the lever</span>
            <span class="lever-bracket">
              <span class="lever-axis"></span>
            </span>
            <span class="lever-rod">
              <span class="lever-link"></span>
              <span class="lever-handle">
                <span class="lever-stem"></span>
                <span class="lever-knob"></span>
              </span>
            </span>
          </button>
        </div>
      </section>

      <div class="sr-only" aria-live="polite">
        Latest result: {{ lastResultText }}
      </div>

      <footer
        class="mt-10 w-full max-w-3xl rounded-2xl border border-slate-800/60 bg-slate-900/60 p-4 text-center text-sm text-slate-400"
      >
        最终解释权归@peanutbo所有
      </footer>
    </main>
  </div>
</template>
